// ============================================
// SOUS-PLATEFORME TÉLÉPHONIE
// ============================================
// Le SAV téléphonique tenu par un agent vocal, vu par l'équipe : les appels
// et leurs comptes rendus, les chiffres, et ce qu'il faut coller dans
// l'agent (ElevenLabs ou autre). Tout ce qui touche à la téléphonie est ici,
// dans services/voix côté serveur, et dans la catégorie « Téléphonie » des
// paramètres : la brique peut être sortie sur son propre sous-domaine sans
// toucher au reste.

import React, { useEffect, useRef, useState } from 'react';
import { Phone, PhoneIncoming, PhoneOutgoing, Clock, FileText, Settings, BarChart3, Copy, Check, ExternalLink, ArrowLeft } from 'lucide-react';
import { AdminApi, type AppelTelephonique, type StatsTelephonie, type ConfigurationAgentVocal } from '../../services/api';
import { Badge, Button, Card, Tabs } from '../../shared/ui';
import { BadgeStatut } from '../../shared/ui/BadgeStatut';
import { formatDate, formatDateHeure, formatDateHeureLongue, formatJour } from '../../shared/dates';

// Mobile et tablette : cibles tactiles d'au moins 40 px. À partir de lg
// (ordinateur), les classes s'annulent et le rendu reste celui d'origine.
const TACTILE = 'min-h-[40px] lg:min-h-0';

function duree(sec: number | null): string {
  if (!sec) return '—';
  const m = Math.floor(sec / 60), s = sec % 60;
  return m ? `${m} min ${s.toString().padStart(2, '0')}` : `${s} s`;
}

const Copier: React.FC<{ texte: string }> = ({ texte }) => {
  const [fait, setFait] = useState(false);
  return (
    <button onClick={() => { navigator.clipboard.writeText(texte).then(() => { setFait(true); setTimeout(() => setFait(false), 1500); }); }} title="Copier" aria-label="Copier" className="p-3 -m-2 lg:p-1 lg:m-0 shrink-0 rounded text-graphite hover:text-encre hover:bg-brume">
      {fait ? <Check className="w-4 h-4 text-sapin-700" /> : <Copy className="w-4 h-4" />}
    </button>
  );
};

const Appels: React.FC<{ onOuvrirDossier: (id: string) => void }> = ({ onOuvrirDossier }) => {
  const [appels, setAppels] = useState<AppelTelephonique[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [ouvert, setOuvert] = useState<AppelTelephonique | null>(null);
  const [chargement, setChargement] = useState(true);
  const detail = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ouvert || typeof window.matchMedia !== 'function' || !window.matchMedia('(max-width: 1023px)').matches) return;
    detail.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, [ouvert]);

  useEffect(() => {
    let vivant = true; setChargement(true);
    AdminApi.telephonieAppels(page).then(r => { if (vivant) { setAppels(r.appels); setTotal(r.total); } }).catch(() => {}).finally(() => { if (vivant) setChargement(false); });
    return () => { vivant = false; };
  }, [page]);

  const voir = async (a: AppelTelephonique) => {
    try { setOuvert(await AdminApi.telephonieAppel(a.id)); } catch { setOuvert(a); }
  };

  if (!chargement && total === 0) {
    return (
      <Card className="p-10 text-center">
        <Phone className="w-10 h-10 text-graphite/50 mx-auto mb-3" />
        <p className="font-semibold text-encre">Aucun appel enregistré</p>
        <p className="text-sm text-graphite mt-1">Les appels apparaîtront ici dès que l'agent vocal sera branché (onglet Configuration).</p>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
      <Card className={`overflow-hidden ${ouvert ? 'lg:col-span-3' : 'lg:col-span-5'}`}>
        <ul data-testid="appels-cartes" className="md:hidden divide-y divide-trait">
          {appels.map(a => (
            <li key={a.id}>
              <button type="button" onClick={() => voir(a)} aria-current={ouvert?.id === a.id ? 'true' : undefined}
                className={`w-full text-left px-4 py-3 hover:bg-brume ${ouvert?.id === a.id ? 'bg-sapin-50' : ''}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-encre truncate">{a.customer?.displayName ?? a.callerName ?? 'Inconnu'}</p>
                    <p className="tnum text-xs text-graphite">{a.phoneNumber ?? 'numéro masqué'}</p>
                  </div>
                  <span className="tnum text-xs text-graphite whitespace-nowrap">{duree(a.durationSec)}</span>
                </div>
                <p className="mt-1 tnum text-xs text-graphite inline-flex items-center gap-1.5">
                  {a.direction === 'SORTANT' ? <PhoneOutgoing className="w-3.5 h-3.5" /> : <PhoneIncoming className="w-3.5 h-3.5 text-sapin-700" />}
                  {formatDateHeure(a.startedAt)}
                </p>
              </button>
              {a.ticket && (
                <div className="px-4 pb-3 -mt-1">
                  <button type="button" onClick={() => onOuvrirDossier(a.ticket!.id)} className="inline-flex items-center gap-2 min-h-[40px] hover:underline">
                    <span className="ref text-sm">{a.ticket.ticketRef}</span>
                    <BadgeStatut status={a.ticket.status} />
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
        <table className="hidden md:table w-full">
          <thead className="bg-white border-b border-trait">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-graphite">Quand</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-graphite">Appelant</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-graphite">Durée</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-graphite">Dossier</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-trait">
            {appels.map(a => (
              <tr key={a.id} onClick={() => voir(a)} className={`cursor-pointer hover:bg-brume ${ouvert?.id === a.id ? 'bg-sapin-50' : ''}`}>
                <td className="px-4 py-3 tnum text-sm text-encre whitespace-nowrap">
                  <span className="inline-flex items-center gap-1.5">{a.direction === 'SORTANT' ? <PhoneOutgoing className="w-4 h-4 text-graphite" /> : <PhoneIncoming className="w-4 h-4 text-sapin-700" />}{formatDateHeure(a.startedAt)}</span>
                </td>
                <td className="px-4 py-3 text-sm">
                  <p className="text-encre">{a.customer?.displayName ?? a.callerName ?? 'Inconnu'}</p>
                  <p className="tnum text-xs text-graphite">{a.phoneNumber ?? 'numéro masqué'}</p>
                </td>
                <td className="px-4 py-3 tnum text-sm text-graphite">{duree(a.durationSec)}</td>
                <td className="px-4 py-3 text-sm">
                  {a.ticket ? (
                    <button onClick={(e) => { e.stopPropagation(); onOuvrirDossier(a.ticket!.id); }} className="inline-flex items-center gap-2 hover:underline">
                      <span className="ref text-sm">{a.ticket.ticketRef}</span>
                      <BadgeStatut status={a.ticket.status} />
                    </button>
                  ) : <span className="text-graphite">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {total > 25 && (
          <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-t border-trait text-sm text-graphite">
            <span className="tnum">{total} appels</span>
            <div className="flex gap-2">
              <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className={TACTILE}>Précédent</Button>
              <Button variant="secondary" size="sm" disabled={page * 25 >= total} onClick={() => setPage(p => p + 1)} className={TACTILE}>Suivant</Button>
            </div>
          </div>
        )}
      </Card>
      {ouvert && (
        <div ref={detail} className="lg:col-span-2 scroll-mt-4">
        <Card className="h-full p-4 sm:p-5 space-y-4">
          {/* Téléphone et tablette : le détail s'affiche sous la liste ; ce bouton y ramène. */}
          <button onClick={() => { setOuvert(null); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className={`lg:hidden inline-flex items-center gap-1.5 text-sm font-semibold text-sapin-700 hover:text-sapin-900 ${TACTILE}`}>
            <ArrowLeft className="w-4 h-4" />Retour à la liste
          </button>
          <div>
            <p className="text-xs font-semibold text-graphite">Appel du {formatDateHeureLongue(ouvert.startedAt)}</p>
            <p className="text-encre font-bold mt-0.5">{ouvert.customer?.displayName ?? ouvert.callerName ?? 'Appelant inconnu'} <span className="tnum font-normal text-graphite">· {ouvert.phoneNumber ?? 'numéro masqué'} · {duree(ouvert.durationSec)}</span></p>
          </div>
          {ouvert.summary && <div><p className="text-xs font-semibold text-graphite mb-1">Résumé</p><p className="text-sm text-encre leading-relaxed">{ouvert.summary}</p></div>}
          {ouvert.recordingUrl && <a href={ouvert.recordingUrl} target="_blank" rel="noreferrer" className={`inline-flex items-center gap-1.5 text-sm font-semibold text-sapin-700 hover:text-sapin-900 ${TACTILE}`}><ExternalLink className="w-4 h-4" />Écouter l'enregistrement</a>}
          {ouvert.transcript && (
            <div>
              <p className="text-xs font-semibold text-graphite mb-1">Transcription</p>
              <pre className="text-xs text-encre whitespace-pre-wrap font-sans leading-relaxed max-h-96 overflow-y-auto bg-brume rounded-lg p-3">{ouvert.transcript}</pre>
            </div>
          )}
          {!ouvert.summary && !ouvert.transcript && <p className="text-sm text-graphite">Aucun compte rendu transmis pour cet appel.</p>}
        </Card>
        </div>
      )}
    </div>
  );
};

const Statistiques: React.FC = () => {
  const [s, setS] = useState<StatsTelephonie | null>(null);
  useEffect(() => { AdminApi.telephonieStats().then(setS).catch(() => {}); }, []);
  if (!s) return <p className="text-sm text-graphite">Chargement…</p>;
  const max = Math.max(1, ...s.parJour.map(j => j.nombre));
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 min-[400px]:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {[
          ['Appels sur 30 jours', s.sur30Jours, 'tous appels confondus'],
          ['Rattachés à un dossier', s.rattachesAUnDossier, 'le reste est à qualifier'],
          ['Dossiers ouverts par téléphone', s.dossiersOuvertsParTelephone30Jours, 'sur 30 jours'],
          ['Durée moyenne', duree(s.dureeMoyenneSec), 'par appel'],
        ].map(([t, v, sub]) => (
          <Card key={String(t)} className="p-4 sm:p-5">
            <p className="text-sm font-semibold text-graphite">{t}</p>
            <p className="tnum text-2xl sm:text-3xl font-extrabold tracking-tight text-encre mt-2">{v}</p>
            <p className="text-xs text-graphite mt-1">{sub}</p>
          </Card>
        ))}
      </div>
      <Card className="p-4 sm:p-5">
        <p className="text-sm font-semibold text-graphite mb-4">Appels par jour, 30 derniers jours</p>
        {s.parJour.length === 0 ? <p className="text-sm text-graphite">Aucun appel sur la période.</p> : (
          <div className="flex items-end gap-px sm:gap-1 h-32">
            {s.parJour.map(j => (
              <div key={String(j.jour)} title={`${/^\d{4}-\d{2}-\d{2}$/.test(String(j.jour)) ? formatJour(String(j.jour), { day: '2-digit', month: '2-digit', year: 'numeric' }) : formatDate(j.jour)} : ${j.nombre}`} className="flex-1 bg-sapin-600 rounded-t" style={{ height: `${(j.nombre / max) * 100}%` }} />
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

const Configuration: React.FC<{ onParametres: () => void }> = ({ onParametres }) => {
  const [c, setC] = useState<ConfigurationAgentVocal | null>(null);
  useEffect(() => { AdminApi.telephonieConfiguration().then(setC).catch(() => {}); }, []);
  if (!c) return <p className="text-sm text-graphite">Chargement…</p>;
  const etat = (ok: boolean, oui: string, non: string) => <Badge className={ok ? 'bg-sapin-50 text-sapin-900' : 'bg-[#FBF1E1] text-[#7A4A00]'}>{ok ? oui : non}</Badge>;
  return (
    <div className="space-y-6 max-w-4xl">
      <Card className="p-4 sm:p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="font-bold text-encre">État de la sous-plateforme</p>
            <p className="text-sm text-graphite mt-1">Trois réglages, tous dans Paramètres → Téléphonie : l'interrupteur, la clé d'API des outils, le secret du webhook de fin d'appel.</p>
          </div>
          <Button variant="secondary" size="sm" onClick={onParametres} className={`w-full sm:w-auto ${TACTILE}`}><Settings className="w-4 h-4 mr-1" />Ouvrir les paramètres</Button>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {etat(c.actif, 'SAV téléphonique activé', 'SAV téléphonique désactivé')}
          {etat(c.cleConfiguree, 'Clé d\'API des outils : renseignée', 'Clé d\'API des outils : à renseigner')}
          {etat(c.webhookConfigure, 'Secret du webhook : renseigné', 'Secret du webhook : à renseigner')}
        </div>
      </Card>

      <Card className="p-4 sm:p-5">
        <p className="font-bold text-encre">Outils à déclarer dans l'agent vocal</p>
        <p className="text-sm text-graphite mt-1">Dans ElevenLabs : Agent → Tools → Add server tool. Chaque outil appelle notre API avec l'en-tête <span className="tnum font-semibold">{c.enTeteAuth}</span>. Les variables entre accolades sont remplies par l'agent.</p>
        <div className="mt-4 space-y-4">
          {c.outils.map(o => (
            <div key={o.nom} className="rounded-lg border border-trait p-3 sm:p-4">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-bold text-encre break-all">{o.nom}</span>
                <Badge className="bg-brume text-graphite">{o.methode}</Badge>
              </div>
              <p className="text-sm text-graphite mt-1">{o.description}</p>
              <div className="mt-2 flex items-center gap-2">
                <code className="text-xs tnum bg-brume rounded px-2 py-1 flex-1 min-w-0 break-all">{o.url}</code>
                <Copier texte={o.url} />
              </div>
              {o.corps && (
                <div className="mt-2 flex items-start gap-2">
                  <pre className="text-xs bg-brume rounded px-2 py-1 flex-1 min-w-0 overflow-x-auto">{JSON.stringify(o.corps, null, 2)}</pre>
                  <Copier texte={JSON.stringify(o.corps, null, 2)} />
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-4 sm:p-5">
        <p className="font-bold text-encre">Webhook de fin d'appel</p>
        <p className="text-sm text-graphite mt-1">Dans ElevenLabs : Settings → Webhooks → Post-call. Événement <span className="font-semibold">{c.webhookFinAppel.evenement}</span>, signé par <span className="font-semibold">{c.webhookFinAppel.signature}</span>. Le compte rendu et la transcription sont rangés dans le dossier en note interne.</p>
        <div className="mt-2 flex items-center gap-2">
          <code className="text-xs tnum bg-brume rounded px-2 py-1 flex-1 min-w-0 break-all">{c.webhookFinAppel.methode} {c.webhookFinAppel.url}</code>
          <Copier texte={c.webhookFinAppel.url} />
        </div>
      </Card>

      <Card className="p-4 sm:p-5">
        <div className="flex items-start sm:items-center justify-between gap-2">
          <p className="font-bold text-encre">Consignes de l'agent (à coller dans son prompt)</p>
          <Copier texte={c.consignesAgent.join('\n')} />
        </div>
        <ul className="mt-3 space-y-1.5 text-sm text-encre list-disc pl-5">
          {c.consignesAgent.map((l, i) => <li key={i}>{l}</li>)}
        </ul>
      </Card>
    </div>
  );
};

export const TelephonieView: React.FC<{ onOuvrirDossier: (id: string) => void; onParametres: () => void }> = ({ onOuvrirDossier, onParametres }) => {
  const [onglet, setOnglet] = useState('appels');
  return (
    <div className="p-4 sm:p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-extrabold tracking-tight text-encre">Téléphonie</h1>
        <p className="text-graphite">Le standard SAV tenu par un agent vocal : il identifie l'appelant, lit l'état d'un dossier, en ouvre un, et dépose le compte rendu. Un conseiller reprend chaque dossier.</p>
      </div>
      <Tabs activeTab={onglet} onChange={setOnglet} tabs={[
        { id: 'appels', label: 'Appels', icon: <Phone className="w-4 h-4" /> },
        { id: 'stats', label: 'Statistiques', icon: <BarChart3 className="w-4 h-4" /> },
        { id: 'configuration', label: 'Configuration', icon: <FileText className="w-4 h-4" /> },
      ]} />
      {onglet === 'appels' && <Appels onOuvrirDossier={onOuvrirDossier} />}
      {onglet === 'stats' && <Statistiques />}
      {onglet === 'configuration' && <Configuration onParametres={onParametres} />}
      <p className="text-xs text-graphite flex items-start sm:items-center gap-1.5"><Clock className="w-3.5 h-3.5 shrink-0 mt-px sm:mt-0" />Les appels sont conservés avec leur transcription. Un appel sans dossier reconnu reste ici, à rattacher à la main.</p>
    </div>
  );
};

export default TelephonieView;
